<script setup lang="ts" generic="TValue">
import {
  type Component,
  computed,
  inject,
  nextTick,
  onMounted,
  onUnmounted,
  ref,
  shallowRef,
  useTemplateRef,
  type VNode,
  watch,
} from "vue";
import { MenuEntry } from "../../models/menu-entry";
import {
  type ControlSize,
  type DropdownType,
  type OptionItems,
  type Placement,
  isRecord,
  resolveItems,
} from "../../models/types";
import BcMenu from "./bc-menu.vue";
import BcInput from "../../components/bc-input.vue";
import {
  CONTROLS_DESIGN_VERSION,
  DEFAULT_CONTROLS_DESIGN_VERSION,
  DEFAULT_DESIGN_VERSION,
  DESIGN_VERSION,
} from "../../models";

export type BcDropdownProps<TProp> = {
  /** Allows to write into the dropdown's input field */
  allowInput?: boolean;
  /** Specifies custom check for disabled items */
  checkDisabled?: (item?: TProp) => boolean;
  /** Specifies the timeout to wait before close the dropdown */
  closeTimeout?: number;
  /** Disables items for selection */
  disabled?: boolean;
  /** Specifies custom validation messages */
  errors?: Map<string, string>;
  textWrap?: boolean;
  /** Expands the dropdown to the width of its parent container */
  fullWidth?: boolean;
  /** Specifies custom calculation for item key */
  getKey?: (item?: TProp) => string;
  /** Specifies custom calculation for item text */
  getText?: (item?: TProp) => string;
  /** Specifies the disabled property (e.g. disabled) for disabel item selection if items are objects */
  itemDisabledProperty?: keyof TProp;
  /** Specifies the key property (e.g. ID) for identification if items are objects */
  itemKeyProperty?: keyof TProp;
  /** Specifies the text property (e.g. name) for display if items are objects */
  itemTextProperty?: keyof TProp;
  /** Specifies the selectable items */
  items: OptionItems<TProp>;
  /** Specifies the number of visible items */
  numberOfVisibleItems?: number;
  /** Specifies a short hint for the select */
  placeholder?: string | number | boolean;
  /** Specifies the placement of the dropdown items compared to the container element */
  placement?: Placement;
  /** Teleport target for the bc-menu. Needed for transformed or fixed containers. */
  teleportTarget?: string;
  /** Specifies that dropdown's offset relative to the selected element container */
  offset?: number;
  /** Specifies that dropdown is read-only */
  readonly?: boolean;
  /** Specifies if selecting an item is required */
  required?: boolean;
  /** Specifies pre-defined sizes for the dropdown */
  size?: ControlSize;
  /** Defines the dropdown's type */
  type?: DropdownType;
};

/** Specifies two-way binding for the dropdown */
const modelValue = defineModel<TValue>();

const {
  allowInput = true,
  checkDisabled,
  closeTimeout = 100,
  disabled = false,
  errors = new Map<string, string>(),
  textWrap = false,
  fullWidth = false,
  getKey,
  getText,
  itemDisabledProperty,
  itemKeyProperty,
  itemTextProperty,
  items,
  numberOfVisibleItems,
  placeholder = undefined,
  placement = "bottom-start",
  offset,
  readonly = false,
  required = false,
  size,
  type = "dropdown",
} = defineProps<BcDropdownProps<TValue>>();

const design = inject(
  DESIGN_VERSION,
  computed(() => DEFAULT_DESIGN_VERSION),
);
const controlsDesign = inject(
  CONTROLS_DESIGN_VERSION,
  computed(() => DEFAULT_CONTROLS_DESIGN_VERSION),
);

const selectorFocus = ref(false);
const menuFocus = ref(false);
const allResItems = shallowRef<TValue[]>([]);
const resItems = shallowRef<TValue[]>([]);
const validity = ref(true);
const showItems = ref(false);
const errorKeys = ref<string[]>([]);
const inputValue = ref<string | undefined>(undefined);

let hideLastCall: number | undefined = undefined;
const sameWidth = ref(false);
const prevValue = ref<string | undefined>("");

const bcInputRef = useTemplateRef("bcInputRef");
const dropdownControlRef = useTemplateRef("dropdownControlRef");
const selectedItemContainerRef = useTemplateRef("selectedItemContainerRef");

const allResolvedItems = computed<TValue[]>({
  get: () => allResItems.value as TValue[],
  set: (val: TValue[]) => {
    allResItems.value = val;
  },
});

const resolvedItems = computed<TValue[]>({
  get: () => resItems.value as TValue[],
  set: (val: TValue[]) => {
    resItems.value = val;
  },
});

const pak = computed<string>(() => {
  if (type == "select") {
    return "bc-controls-d7";
  }

  return `bc-controls-d${controlsDesign.value}`;
});

const selectedIdx = computed<number>(() => {
  if (typeof modelValue.value !== "undefined") {
    return resolvedItems.value.findIndex((item) =>
      calculateKey(item) !== undefined && calculateKey(modelValue.value) !== undefined
        ? calculateKey(item) === calculateKey(modelValue.value)
        : calculateText(item) === calculateText(modelValue.value),
    );
  }
  return resolvedItems.value
    ? resolvedItems.value.findIndex((item) => calculateText(item) === inputValue.value)
    : -1;
});

const selectedItem = computed<TValue | undefined>(() => {
  if (typeof modelValue.value !== "undefined") {
    return resolvedItems.value.find((item) =>
      calculateKey(item) !== undefined && calculateKey(modelValue.value) !== undefined
        ? calculateKey(item) === calculateKey(modelValue.value)
        : calculateText(item) === calculateText(modelValue.value),
    );
  }
  return resolvedItems.value
    ? resolvedItems.value.find((item) => calculateText(item) === inputValue.value)
    : undefined;
});

const menuItems = computed(() => {
  return MenuEntry.getMenuEntries(
    resolvedItems.value,
    itemTextProperty,
    getText,
    itemKeyProperty,
    getKey,
    itemDisabledProperty,
    checkDisabled,
  );
});

const selectedItemEntry = computed<MenuEntry | undefined>(() => {
  if (selectedItem.value == null) return;
  const sItem = selectedItem.value;
  const item = new MenuEntry(calculateText(sItem), calculateKey(sItem));
  item.disabled = getDisabled(sItem);
  item.icon =
    typeof sItem === "object" && "icon" in sItem
      ? (sItem.icon as Component | VNode)
      : undefined;
  return item;
});

onMounted(async () => {
  let text = calculateText(modelValue.value) ?? inputValue.value;
  inputValue.value = modelValue.value === undefined ? undefined : text;
  allResolvedItems.value = await resolveItems(items);
  resolvedItems.value = allResolvedItems.value;
  await nextTick();
  if (type === "select") {
    document.addEventListener("click", handleClickEvent);
    document.addEventListener("keydown", handleKeydownEvent);
  }
  text = calculateText(selectedItem.value) ?? inputValue.value;
  inputValue.value = selectedItem.value === undefined ? undefined : text;
  onModelChanged();
  prevValue.value = inputValue.value;
});

onUnmounted(() => {
  if (type === "select") {
    document.removeEventListener("click", handleClickEvent);
    document.removeEventListener("keydown", handleKeydownEvent);
  }
});

const show = async function (): Promise<void> {
  if (disabled || readonly || showItems.value) {
    return;
  }

  window.clearTimeout(hideLastCall);

  try {
    allResolvedItems.value = await resolveItems(items);
    resolvedItems.value = allResolvedItems.value;
    if (resolvedItems.value.length) {
      onModelChanged();
    }
  } catch (error) {
    if (!(error instanceof Error) || error?.message !== "canceled") {
      console.error(error);
    }
  }

  showItems.value = true;
};

function hide(): void {
  window.clearTimeout(hideLastCall);
  hideLastCall = window.setTimeout(() => {
    showItems.value = false;
    bcInputRef.value?.inputRef?.blur();
    dropdownControlRef.value?.blur();
  }, closeTimeout);
}

function getDisabled(item: TValue | undefined): boolean {
  if (checkDisabled) {
    const disabled = checkDisabled(item);
    if (typeof disabled === "boolean") {
      return disabled;
    } else {
      console.warn("Custom disabled check function is invalid.");
    }
  } else if (
    isRecord(item) &&
    itemDisabledProperty &&
    itemDisabledProperty in item &&
    typeof item[itemDisabledProperty] === "boolean"
  ) {
    return item[itemDisabledProperty] as boolean;
  }
  return false;
}

function getValidationMessage(errorKey: string): string {
  return errors.get(errorKey) ?? errorKey;
}

function checkValidity(): boolean {
  errorKeys.value = [];

  if (disabled || readonly) {
    validity.value = true;
    return validity.value;
  }

  if (required && !resolvedItems.value.length) {
    setError("noItems", "No items available");
  } else if (required && (inputValue.value == null || inputValue.value === "")) {
    setError("valueMissing", "Value missing");
  } else if (
    getDisabled(
      resolvedItems.value.find((item) => calculateText(item) === inputValue.value),
    )
  ) {
    setError("disabledItem", "Disabled item");
  } else if (
    bcInputRef.value &&
    resolvedItems.value.every(
      (item) => calculateText(item) !== bcInputRef.value?.modelValue,
    )
  ) {
    setError("invalidItem", "Invalid item");
  } else {
    validity.value = true;
  }

  return validity.value;
}

async function selectItem(item: TValue | undefined): Promise<void> {
  if (getDisabled(item)) return;

  if (resolvedItems.value.find((resolvedItem) => resolvedItem === item)) {
    if (modelValue?.value !== item) {
      const text = calculateText(item) ?? inputValue.value;
      inputValue.value = item === undefined ? undefined : text;
      modelValue.value = item;
    }
    await nextTick();
    prevValue.value = inputValue.value;
    await nextTick();
    checkValidity();
  }
}

async function onMenuClick(entry: MenuEntry): Promise<void> {
  const item = resolvedItems.value.find((resolvedItem) =>
    calculateKey(resolvedItem) !== undefined
      ? calculateKey(resolvedItem) === entry.key
      : calculateText(resolvedItem) === entry.text,
  );

  if (item) {
    selectItem(item);
    hide();
  }
}

const moveSelection = async function (direction: number): Promise<void> {
  if (disabled || readonly) return;

  let newSelectedIdx = selectedIdx.value + direction;
  while (
    newSelectedIdx > 0 &&
    newSelectedIdx < resolvedItems.value.length - 1 &&
    getDisabled(resolvedItems.value[newSelectedIdx])
  ) {
    newSelectedIdx += direction;
  }

  selectItem(resolvedItems.value[newSelectedIdx]);
};

function calculateKey(item?: TValue): string | undefined {
  if (getKey) {
    return getKey(item);
  }
  if (isRecord(item) && itemKeyProperty && itemKeyProperty in item) {
    return item[itemKeyProperty]?.toString() ?? "";
  }
  return undefined;
}

function calculateText(item?: TValue): string {
  if (getText) {
    return getText(item);
  }
  if (isRecord(item) && itemTextProperty && itemTextProperty in item) {
    return item[itemTextProperty]?.toString() ?? "";
  }
  return item?.toString() ?? "";
}

/** Checks item properties */
function checkItemProperties(): void {
  if (
    itemKeyProperty &&
    resolvedItems.value?.some(
      (resolvedItem) =>
        itemKeyProperty &&
        (typeof resolvedItem !== "object" ||
          (typeof resolvedItem === "object" &&
            resolvedItem !== null &&
            !(itemKeyProperty in resolvedItem))),
    )
  ) {
    console.error(
      `Item key property '${itemKeyProperty.toString()}' is not available for all items.`,
    );
  }
  if (
    itemKeyProperty &&
    modelValue.value &&
    (typeof modelValue.value !== "object" ||
      (typeof modelValue.value === "object" &&
        modelValue.value !== null &&
        !(itemKeyProperty in modelValue.value)))
  ) {
    console.error(
      `Item key property '${itemKeyProperty.toString()}' is not available in the model.`,
    );
  }

  if (
    !itemTextProperty &&
    !getText &&
    resolvedItems.value?.some(
      (resolvedItem) => typeof resolvedItem === "object" && resolvedItem !== null,
    )
  ) {
    console.error("Item text property is not defined for object items.");
  }
  if (
    !itemTextProperty &&
    !getText &&
    typeof modelValue.value === "object" &&
    modelValue.value !== null
  ) {
    console.error("Item text property is not defined for model.");
  }
  if (
    itemTextProperty &&
    resolvedItems.value?.some(
      (resolvedItem) =>
        itemTextProperty &&
        (typeof resolvedItem !== "object" ||
          (typeof resolvedItem === "object" &&
            resolvedItem !== null &&
            !(itemTextProperty in resolvedItem))),
    )
  ) {
    console.error(
      `Item text property '${itemTextProperty.toString()}' is not available for all items.`,
    );
  }
  if (
    itemTextProperty &&
    modelValue.value &&
    (typeof modelValue.value !== "object" ||
      (typeof modelValue.value === "object" &&
        modelValue.value !== null &&
        !(itemTextProperty in modelValue.value)))
  ) {
    console.error(
      `Item text property '${itemTextProperty.toString()}' is not available in the model.`,
    );
  }

  if (
    itemDisabledProperty &&
    resolvedItems.value?.some(
      (resolvedItem) =>
        itemDisabledProperty &&
        isRecord(resolvedItem) &&
        itemDisabledProperty in resolvedItem &&
        resolvedItem[itemDisabledProperty] !== null &&
        typeof resolvedItem[itemDisabledProperty] !== "boolean",
    )
  ) {
    console.error("Disabled value is not boolean in one of the items.");
  }
  if (
    itemDisabledProperty &&
    modelValue.value &&
    typeof modelValue.value === "object" &&
    modelValue.value !== null &&
    itemDisabledProperty in modelValue.value &&
    modelValue.value[itemDisabledProperty] !== null &&
    typeof modelValue.value[itemDisabledProperty] !== "boolean"
  ) {
    console.error("Disabled value is not boolean in model.");
  }
}

function handleClickEvent(event: MouseEvent): void {
  if (
    type === "select" &&
    dropdownControlRef.value &&
    event.target !== dropdownControlRef.value &&
    !dropdownControlRef.value.contains(event.target as Node) &&
    showItems.value
  ) {
    hide();
  }
}

function handleKeydownEvent(event: KeyboardEvent): void {
  if ((event.key === "Escape" || event.key === "Enter") && showItems.value) {
    hide();
  }
}

function onDisabledOrReadonlyChanged(value: boolean): void {
  // Hide item list and validation message
  if (value) {
    hide();
  }
}

function onErrorsChanged(): void {
  nextTick(async () => {
    if (!disabled && !readonly) {
      await nextTick();
      checkValidity();
    }
  });
}

async function onItemsChanged(): Promise<void> {
  allResolvedItems.value = await resolveItems(items);
  resolvedItems.value = allResolvedItems.value;
  await nextTick();
  const text = calculateText(selectedItem.value) ?? inputValue.value;
  inputValue.value = selectedItem.value === undefined ? undefined : text;
  onModelChanged();
  prevValue.value = inputValue.value;
}

function onModelChanged(): void {
  nextTick(() => {
    if (typeof modelValue.value !== "undefined") {
      if (!validity.value) {
        checkValidity();
      }
      checkItemProperties();
    }
  });
}

function setError(messageKey: string, messageText: string): void {
  errorKeys.value.push(messageKey);
  if (!errors.has(messageKey)) {
    errors.set(messageKey, messageText);
  }
  if (bcInputRef.value?.inputRef)
    bcInputRef.value.inputRef.title = getValidationMessage(messageText);
  validity.value = false;
}

async function onInputTextChange(inputText?: string): Promise<void> {
  if (disabled || readonly) return;

  try {
    resolvedItems.value = allResolvedItems.value.filter((item) =>
      calculateText(item)
        ?.toUpperCase()
        .includes(inputText?.toUpperCase() ?? ""),
    );
  } catch (error) {
    if (!(error instanceof Error) || error?.message !== "canceled") {
      console.error(error);
    }
  }
}

async function setInputItem(): Promise<void> {
  if (disabled || readonly) return;

  if (!allowInput) {
    hide();
    return;
  }

  window.setTimeout(async () => {
    if (!inputValue.value || inputValue.value == "") return;

    const selectedInputItem = resolvedItems.value.find(
      (item) => calculateText(item) === inputValue.value,
    );

    if (selectedInputItem && !getDisabled(selectedInputItem)) {
      const text = calculateText(selectedInputItem) ?? inputValue.value;
      inputValue.value = selectedInputItem === undefined ? undefined : text;
      modelValue.value = selectedInputItem;
      hide();
      await nextTick();
      prevValue.value = inputValue.value;
    }
  }, 0);
  await nextTick();
  checkValidity();
}

function onFocus(): void {
  if (!showItems.value && !disabled && allowInput && bcInputRef.value?.inputRef) {
    bcInputRef.value.inputRef.focus();
    show();
  }
}

async function onEscape(): Promise<void> {
  if (!bcInputRef.value) return;

  if (allowInput) {
    bcInputRef.value.modelValue = prevValue.value;
  }
  await nextTick();
  hideLastCall = window.setTimeout(() => {
    resolvedItems.value = allResolvedItems.value;
    hide();
  }, closeTimeout);
}

function onDropDownMouseEnter(): void {
  selectorFocus.value = true;
  if (type === "dropdown") show();
}

function onDropDownMouseLeave(): void {
  selectorFocus.value = false;
  setTimeout(() => {
    if (type === "dropdown" && !menuFocus.value) hide();
  }, 0);
}

function onMenuMouseLeave(): void {
  menuFocus.value = false;
  setTimeout(() => {
    if (type === "dropdown" && !selectorFocus.value) hide();
  }, 0);
}

watch(() => items, onItemsChanged);
watch(modelValue, onModelChanged, { deep: true });
watch(() => disabled, onDisabledOrReadonlyChanged);
watch(() => readonly, onDisabledOrReadonlyChanged);
watch(() => errors, onErrorsChanged, { deep: true });

defineExpose({
  modelValue,
  resolvedItems,
  selectedIdx,
  validity,
  showItems,
  errorKeys,
  sameWidth,
  bcInputRef,
  dropdownControlRef,
  inputValue,
  selectedItem,
  show,
  hide,
  getValidationMessage,
  checkValidity,
  selectItem,
  moveSelection,
});
</script>
<template>
  <div
    ref="dropdownControlRef"
    v-bind:data-pak="pak"
    v-bind:data-design="design"
    class="bissantzControls dropdownControl"
    v-bind:class="[
      size ? `dropdown-${size}` : '',
      {
        fullWidth: fullWidth,
        disabled: disabled,
        readonly: readonly,
      },
    ]"
    v-bind:tabindex="!disabled ? 0 : -1"
    v-bind:data-type="type"
    v-on:focus="onFocus()"
    v-on:keydown.esc.stop.prevent="onEscape()"
    v-on:keydown.up.stop.prevent="moveSelection(-1)"
    v-on:keydown.down.stop.prevent="moveSelection(1)"
    v-on:mouseleave="onDropDownMouseLeave"
    v-on:mouseenter="onDropDownMouseEnter"
  >
    <div
      ref="selectedItemContainerRef"
      class="selectedItemContainer"
      v-bind:class="{ disabled: disabled, readonly: readonly, invalid: !validity }"
    >
      <template v-if="type === 'dropdown'">
        <slot
          name="before-item"
          v-bind:item="selectedItem"
          v-bind:entry="selectedIdx !== -1 ? menuItems[selectedIdx] : undefined"
          v-bind:itemIdx="selectedIdx"
          v-bind:itemKey="
            selectedItem !== undefined && calculateKey(selectedItem) !== undefined
              ? calculateKey(selectedItem)
              : -1
          "
          v-bind:itemText="selectedItem !== undefined ? calculateText(selectedItem) : ''"
        >
          <component
            v-if="isRecord(selectedItem) && selectedItem?.icon"
            v-bind:is="selectedItem.icon"
          />
        </slot>
        <BcInput
          ref="bcInputRef"
          v-on:focus="prevValue = inputValue"
          v-on:blur="setInputItem"
          v-on:keydown.enter.stop.prevent="setInputItem"
          v-on:input="onInputTextChange"
          v-on:click.stop.prevent="show"
          fullWidth
          v-bind:modelValue="inputValue"
          class="dropdownText"
          v-bind:class="{ invalid: !validity }"
          v-bind:errors="errors"
          v-bind:readonly="!allowInput || readonly || disabled"
          v-bind:size="size"
          v-bind:placeholder="placeholder"
          tabindex="-1"
        />
      </template>
      <template v-if="type === 'select'">
        <div class="selectContainer" v-on:click="showItems ? hide() : show()">
          <div class="selectedItem" v-bind:tabindex="!disabled ? 0 : -1">
            <slot
              name="before-item"
              v-bind:item="selectedItem"
              v-bind:entry="selectedIdx !== -1 ? menuItems[selectedIdx] : undefined"
              v-bind:itemIdx="selectedIdx"
              v-bind:itemKey="
                selectedItem !== undefined && calculateKey(selectedItem) !== undefined
                  ? calculateKey(selectedItem)
                  : -1
              "
              v-bind:itemText="
                selectedItem !== undefined ? calculateText(selectedItem) : ''
              "
            >
              <component
                v-if="isRecord(selectedItem) && selectedItem?.icon"
                v-bind:is="selectedItem.icon"
              />
            </slot>
            <span
              v-bind:data-locid="
                selectedItem === undefined
                  ? $attrs['data-placeholder-locid']
                  : calculateText(selectedItem)
              "
              v-bind:data-locparam="
                selectedItem === undefined
                  ? $attrs['data-placeholder-locparam']
                  : undefined
              "
              v-bind:data-locparam1="
                selectedItem === undefined
                  ? $attrs['data-placeholder-locparam1']
                  : undefined
              "
              v-bind:data-locparam2="
                selectedItem === undefined
                  ? $attrs['data-placeholder-locparam2']
                  : undefined
              "
            >
              {{ selectedItem === undefined ? placeholder : inputValue }}
            </span>
          </div>
          <svg
            xmlns="http://www.w3.org/2000/svg"
            height="30"
            viewBox="0 -960 960 960"
            width="30"
          >
            <path
              d="M480-357q-6 0-11-2t-10-7L261-564q-9-9-9-21t9-21q9-9 21.5-9t21.5 9l176 176 176-176q9-9 21-9t21 9q9 9 9 21.5t-9 21.5L501-366q-5 5-10 7t-11 2Z"
            />
          </svg>
        </div>
      </template>
    </div>
    <div
      class="validationMessage"
      v-if="!disabled && !readonly && !validity && errorKeys.length"
    >
      <span v-for="(errorKey, idx) in errorKeys" v-bind:key="idx">
        {{ getValidationMessage(errorKey) }}
      </span>
    </div>
    <BcMenu
      class="bc-dropdown"
      v-bind:numberOfVisibleItems="numberOfVisibleItems"
      v-bind:separateAll="true"
      v-bind:isVisible="showItems"
      v-bind:items="menuItems"
      v-bind:offset="(offset ?? type === 'dropdown') ? -1 : 0"
      v-bind:placement="placement"
      v-bind:fullWidth="true"
      v-bind:key="menuItems.length"
      v-bind:textWrap="textWrap"
      v-bind:parentRect="selectedItemContainerRef"
      v-bind:selectedItem="selectedItemEntry"
      v-on:click="onMenuClick"
      v-bind:teleportTarget="teleportTarget"
      v-bind:data-type="type"
      v-on:mouseenter="menuFocus = true"
      v-on:mouseleave="onMenuMouseLeave"
    >
      <template v-slot:before-item="{ entryIdx, entry }">
        <slot
          name="before-item"
          v-bind:item="resolvedItems[entryIdx]"
          v-bind:entry="entry"
          v-bind:itemIdx="entryIdx"
          v-bind:itemKey="
            calculateKey(resolvedItems[entryIdx]) !== undefined
              ? calculateKey(resolvedItems[entryIdx])
              : entryIdx
          "
          v-bind:itemText="calculateText(resolvedItems[entryIdx])"
        />
      </template>
    </BcMenu>
  </div>
</template>

<style lang="less">
@import "../../styles/components/menu/bc-dropdown.less";
</style>
